import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { toast } from 'sonner';
import { School } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const EMERGENT_AUTH_URL = 'https://auth.emergentagent.com/';

const LoginPage = () => {
  const { login } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    const hash = window.location.hash;
    if (hash && hash.includes('session_id=')) {
      const sessionId = hash.split('session_id=')[1];
      handleSessionId(sessionId);
    }
  }, []);

  const handleSessionId = async (sessionId) => {
    try {
      const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
      const { session_token, user_id } = response.data;
      
      // Get user data
      const userResponse = await axios.get(`${API}/auth/me`, {
        headers: { Authorization: `Bearer ${session_token}` }
      });
      
      login(session_token, userResponse.data);
      
      // Clear the hash from URL
      window.history.replaceState(null, null, ' ');
      
      // Check if user needs to select a role
      if (!userResponse.data.role) {
        navigate('/role-selection');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      console.error('Session creation failed:', error);
      console.error('Error details:', error.response?.data || error.message);
      const errorMessage = error.response?.data?.detail || error.message || 'Authentication failed';
      toast.error(`Authentication failed: ${errorMessage}`);
    }
  };

  const handleGoogleLogin = () => {
    const redirectUrl = encodeURIComponent(`${window.location.origin}/login`);
    window.location.href = `${EMERGENT_AUTH_URL}?redirect=${redirectUrl}`;
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-sky-50 to-white p-4">
      <Card className="w-full max-w-md p-8 rounded-2xl shadow-lg border-slate-100 bg-white" data-testid="login-card">
        <div className="text-center mb-8">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-sky-100 rounded-full mb-4">
            <School className="w-10 h-10 text-sky-500" />
          </div>
          <h1 className="text-4xl font-bold text-slate-900 mb-2 font-heading" data-testid="app-title">Penguin Montessori</h1>
          <p className="text-slate-500 text-lg">School Attendance System</p>
        </div>

        <div className="space-y-6">
          <div className="text-center">
            <p className="text-slate-600 mb-4">Sign in to continue</p>
            <Button
              onClick={handleGoogleLogin}
              className="w-full bg-sky-500 hover:bg-sky-600 text-white rounded-full py-6 text-lg font-semibold shadow-sm transition-all hover:shadow-md active:scale-95"
              data-testid="google-login-btn"
            >
              <svg className="w-6 h-6 mr-3" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Sign in with Google
            </Button>
          </div>

          <div className="text-center text-sm text-slate-500">
            <p>Available for Admin, Teacher & Parent roles</p>
          </div>
        </div>
      </Card>
    </div>
  );
};

export default LoginPage;